/**
 * Workspace Store
 * Manages the active workspace (academy)
 *
 * @description Zustand store for workspace state management.
 *
 * ISSUE_182: 워크스페이스 = 학원 단일. scratch(연습장) 폐기.
 * `active`는 학원이 선택됐을 때만 academy, 미선택이면 null(런처가 게이트).
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { supabase, isSupabaseConfigured } from '~/shared/lib/supabase';
import { clearSpaceEntered } from '~/core/utils/spaceEntry';

// ============================================================================
// CONSTANTS
// ============================================================================

const FETCH_TIMEOUT = 10 * 1000; // 10 seconds - prevent infinite hanging

// ============================================================================
// NETWORK & TIMEOUT HELPERS
// ============================================================================

class OfflineError extends Error {
  constructor(message = 'Network is offline') {
    super(message);
    this.name = 'OfflineError';
  }
}

/**
 * Check if network is online before making request
 * Throws OfflineError if offline - enables fail-fast behavior
 */
function checkOnline(): void {
  if (typeof navigator !== 'undefined' && !navigator.onLine) {
    throw new OfflineError();
  }
}

/**
 * Wraps a promise or thenable with a timeout and network check
 */
function withTimeout<T>(promiseOrThenable: Promise<T> | PromiseLike<T>, ms: number): Promise<T> {
  // Check network first - fail fast if offline
  checkOnline();

  const promise = Promise.resolve(promiseOrThenable);

  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      reject(new Error(`Request timed out after ${ms}ms`));
    }, ms);

    promise
      .then((result) => {
        clearTimeout(timer);
        resolve(result);
      })
      .catch((error) => {
        clearTimeout(timer);
        reject(error);
      });
  });
}

// ============================================================================
// TYPES
// ============================================================================

/** Academy info in workspace */
export interface WorkspaceAcademy {
  id: string;
  name: string;
  role: 'owner' | 'admin' | 'manager' | 'teacher';
  color?: string;
  isPrimary?: boolean;
  roleLabels?: Record<string, string> | null;
}

/** Active workspace — 학원 단일 (ISSUE_182). 미선택 상태는 store의 active=null. */
export type ActiveWorkspace = {
  type: 'academy';
  id: string;
  name: string;
  role: WorkspaceAcademy['role'];
  roleLabels?: Record<string, string> | null;
};

/** Workspace state shape */
export interface WorkspaceState {
  /** Currently active workspace. id==='' = 미선택(런처가 게이트). */
  active: ActiveWorkspace;
  /** List of academies user belongs to */
  academies: WorkspaceAcademy[];
  /** Whether academies are being loaded */
  isLoading: boolean;
  /** Error message if loading failed */
  error: string | null;
  /** Last sync timestamp */
  lastSync: number | null;
  /** Last loaded profile ID (to prevent duplicate loads) */
  _lastLoadedProfileId: string | null;
}

/** Workspace actions */
export interface WorkspaceActions {
  /** Switch to an academy workspace */
  switchToAcademy: (academyId: string) => Promise<void>;
  /** Load academies from server */
  loadAcademies: (profileId: string) => Promise<void>;
  /** Set loading state */
  setLoading: (loading: boolean) => void;
  /** Set error */
  setError: (error: string | null) => void;
  /** Clear workspace state (on sign out) */
  clearWorkspace: () => void;
  /** Initialize workspace from cached context */
  initializeFromContext: (profileId: string) => Promise<void>;
  /** Update role labels for current academy */
  updateRoleLabels: (academyId: string, roleLabels: Record<string, string> | null) => void;
}

/** Combined store type */
export type WorkspaceStore = WorkspaceState & WorkspaceActions;

// ============================================================================
// DEFAULT STATE
// ============================================================================

/** 미선택 센티넬 — id==='' 이면 "아직 학원 안 고름"(런처가 게이트). ISSUE_182. */
export const EMPTY_WORKSPACE: ActiveWorkspace = {
  type: 'academy',
  id: '',
  name: '',
  role: 'teacher',
  roleLabels: null,
};

const DEFAULT_STATE: WorkspaceState = {
  active: EMPTY_WORKSPACE,
  academies: [],
  isLoading: false,
  error: null,
  lastSync: null,
  _lastLoadedProfileId: null,
};

const WORKSPACE_PERSIST_KEY = 'noten_workspace_store';

// ============================================================================
// HELPERS
// ============================================================================

/**
 * Save active context to user_contexts table
 */
async function saveContextToServer(profileId: string, academyId: string): Promise<void> {
  if (!isSupabaseConfigured() || !supabase) return;
  // ISSUE_063: 오프라인이면 skip (context save는 비필수 작업)
  if (typeof navigator !== 'undefined' && !navigator.onLine) return;

  try {
    const result = await withTimeout(
      supabase
        .from('user_contexts')
        .upsert(
          {
            user_id: profileId,
            active_academy_id: academyId,
            updated_at: new Date().toISOString(),
          },
          { onConflict: 'user_id' }
        ),
      FETCH_TIMEOUT
    );
    if (result.error) {
      // Context save failure is non-critical; silently ignored
    }
  } catch {
    // Context save is not critical — silently ignored
  }
}

/**
 * Load saved context from user_contexts table
 */
async function loadContextFromServer(profileId: string): Promise<string | null> {
  if (!isSupabaseConfigured() || !supabase) return null;
  // ISSUE_063: 오프라인이면 null 반환 (persist된 active workspace 유지)
  if (typeof navigator !== 'undefined' && !navigator.onLine) return null;

  try {
    const result = await withTimeout(
      supabase
        .from('user_contexts')
        .select('active_academy_id')
        .eq('user_id', profileId)
        .single(),
      FETCH_TIMEOUT
    );

    return result.data?.active_academy_id ?? null;
  } catch {
    return null;
  }
}

// ============================================================================
// STORE IMPLEMENTATION
// ============================================================================

/**
 * useWorkspaceStore - Workspace State Management
 *
 * 학원 단일 컨텍스트 관리 (ISSUE_182). active=null 이면 미선택(런처가 게이트).
 *
 * @example
 * ```tsx
 * function WorkspaceHeader() {
 *   const name = useWorkspaceName();
 *   return <span>{name}</span>;
 * }
 * ```
 */
export const useWorkspaceStore = create<WorkspaceStore>()(
  persist(
    (set, get) => ({
      // State
      ...DEFAULT_STATE,

      // Actions
      switchToAcademy: async (academyId: string) => {
        const { academies } = get();
        const academy = academies.find((a) => a.id === academyId);

        if (!academy) {
          return;
        }

        // ISSUE_139: resetToDummyData + set(active)를 먼저 실행하여 stale window 차단
        const { useSessionsStore } = await import('../useSessionsStore');
        useSessionsStore.getState().resetToDummyData();
        set({
          active: {
            type: 'academy',
            id: academy.id,
            name: academy.name,
            role: academy.role,
            roleLabels: academy.roleLabels ?? null,
          },
        });
        // save는 fire-and-forget (실패해도 다음 전환 시 재시도)
        const profileId = getProfileIdFromAuth();
        if (profileId) {
          saveContextToServer(profileId, academyId).catch(() => {});
        }
      },

      loadAcademies: async (profileId: string) => {
        // Prevent duplicate loads for same profile (only skip if we actually have data)
        if (get()._lastLoadedProfileId === profileId && get().academies.length > 0 && get().lastSync) {
          return;
        }

        if (!isSupabaseConfigured()) {
          set({ academies: [], isLoading: false, _lastLoadedProfileId: profileId });
          return;
        }

        // ISSUE_063: 오프라인이면 persist된 캐시 유지, 네트워크 요청 스킵
        if (typeof navigator !== 'undefined' && !navigator.onLine) {
          set({ isLoading: false, _lastLoadedProfileId: profileId });
          return;
        }

        set({ isLoading: true, error: null });

        try {
          // Fetch academies through teacher_academies join
          if (!supabase) {
            set({ academies: [], isLoading: false, lastSync: Date.now(), _lastLoadedProfileId: profileId });
            return;
          }
          const result = await withTimeout(
            supabase
              .from('teacher_academies')
              .select(`
                academy_id,
                is_primary,
                role,
                academies!inner (
                  id,
                  name,
                  role_labels
                )
              `)
              .eq('teacher_id', profileId),
            FETCH_TIMEOUT
          );
          const { data, error } = result;

          if (error) {
            set({
              academies: [],
              isLoading: false,
              lastSync: Date.now(),
              _lastLoadedProfileId: profileId,
            });
            return;
          }

          const academies: WorkspaceAcademy[] = (data ?? []).map((ta: any) => ({
            id: ta.academies.id,
            name: ta.academies.name,
            role: (ta.role?.toLowerCase() ?? 'teacher') as WorkspaceAcademy['role'],
            isPrimary: ta.is_primary,
            roleLabels: ta.academies.role_labels ?? null,
          }));

          set({
            academies,
            isLoading: false,
            lastSync: Date.now(),
            _lastLoadedProfileId: profileId,
          });
        } catch (err) {
          const msg = err instanceof Error ? err.message : String(err);
          const isTransient = msg.includes('timed out') ||
            msg.includes('AbortError') ||
            msg.includes('Failed to fetch') ||
            msg.includes('NetworkError') ||
            msg.includes('offline');

          if (isTransient && get().academies.length > 0) {
            // Transient error: keep cached academies, don't destroy workspace
            set({ isLoading: false });
          } else {
            set({
              academies: [],
              isLoading: false,
              lastSync: Date.now(),
              _lastLoadedProfileId: profileId,
            });
          }
        }
      },

      setLoading: (loading: boolean) => set({ isLoading: loading }),

      setError: (error: string | null) => set({ error }),

      clearWorkspace: () => {
        set(DEFAULT_STATE);
        clearSpaceEntered(); // ISSUE_182(a): 로그아웃 → 다음 로그인 시 런처
      },

      updateRoleLabels: (academyId: string, roleLabels: Record<string, string> | null) => {
        const { active, academies } = get();
        // Update in academies list
        const updated = academies.map((a) =>
          a.id === academyId ? { ...a, roleLabels } : a
        );
        // Update in active workspace if it's the current academy
        if (active.id === academyId) {
          set({
            academies: updated,
            active: { ...active, roleLabels },
          });
        } else {
          set({ academies: updated });
        }
      },

      initializeFromContext: async (profileId: string) => {
        // Remember current state before loading (may be persisted cache)
        const prevActive = get().active;

        // Load fresh academies from server
        await get().loadAcademies(profileId);

        const { academies } = get();

        // NO academies — 캐시된 학원이 있으면 유지(일시적 로드 실패 가능), 없으면 미선택(null)
        if (academies.length === 0) {
          if (prevActive.id !== '') {
            // Keep cached workspace — likely a transient load failure
            return;
          }
          set({ active: EMPTY_WORKSPACE });
          return;
        }

        // Validate cached active workspace - if it's an academy that no longer exists, reset
        const { active } = get();
        if (active.id !== '') {
          const stillExists = academies.some((a) => a.id === active.id);
          if (!stillExists) {
            set({ active: EMPTY_WORKSPACE });
          }
        }

        // ISSUE_063: 오프라인이면 persisted workspace를 신뢰하고 서버 context 스킵
        if (typeof navigator !== 'undefined' && !navigator.onLine) {
          return;
        }

        // Then restore saved context from server (if any)
        const savedAcademyId = await loadContextFromServer(profileId);

        if (savedAcademyId) {
          const academy = academies.find((a) => a.id === savedAcademyId);

          if (academy) {
            set({
              active: {
                type: 'academy',
                id: academy.id,
                name: academy.name,
                role: academy.role,
                roleLabels: academy.roleLabels ?? null,
              },
            });
            return;
          }
        }

        // 저장된 컨텍스트 없음 → active는 그대로(persist된 학원 또는 null). 런처가 미선택을 게이트.
      },
    }),
    {
      name: WORKSPACE_PERSIST_KEY,
      // Persist active workspace and academies list
      partialize: (state) => ({
        active: state.active,
        academies: state.academies,
        lastSync: state.lastSync,
      }),
      // Skip hydration during SSR
      skipHydration: typeof window === 'undefined',
    }
  )
);

// ============================================================================
// HELPER: Get profile ID from auth store
// ============================================================================

/**
 * Helper to get current profile ID from auth store
 * We import dynamically to avoid circular dependencies
 */
function getProfileIdFromAuth(): string | null {
  try {
    const raw = localStorage.getItem('noten_auth_store') ?? '{}';
    const authState: unknown = JSON.parse(raw);
    if (!authState || typeof authState !== "object") return null;
    const state = (authState as Record<string, unknown>)["state"];
    if (!state || typeof state !== "object") return null;
    const profile = (state as Record<string, unknown>)["profile"];
    if (!profile || typeof profile !== "object") return null;
    return (profile as Record<string, unknown>)["id"] as string ?? null;
  } catch {
    return null;
  }
}

// ============================================================================
// SELECTOR HOOKS
// ============================================================================

/**
 * 학원이 선택돼 있는지 (active !== null)
 */
export const useIsAcademyWorkspace = () =>
  useWorkspaceStore((state) => state.active.id !== '');

/**
 * Get current workspace name ('' if none)
 */
export const useWorkspaceName = () =>
  useWorkspaceStore((state) => state.active.name);

/**
 * Get current academy ID (null if none)
 */
export const useCurrentAcademyId = () =>
  useWorkspaceStore((state) => state.active.id || null);

/**
 * Get current workspace role
 */
export const useWorkspaceRole = () =>
  useWorkspaceStore((state) => state.active.role);

/**
 * Get list of academies
 */
export const useAcademyList = () =>
  useWorkspaceStore((state) => state.academies);

/**
 * Check if user has any academies
 */
export const useHasAcademies = () =>
  useWorkspaceStore((state) => state.academies.length > 0);

export default useWorkspaceStore;
